export class ChatsApi {
  #http;
  #skynetHttp;

  constructor(http, skynetHttp) {
    this.#http = http;
    this.#skynetHttp = skynetHttp;
  }

  async getChatInfo(params) {
    return this.#http.get('api/Chats', { params });
  }

  async getUserChatsWithLoanHeaders() {
    // DEV-3647: this header-shell call 403s for TPO broker users who lack loan/chat access. Opt out of the
    // global forbidden-status interceptor so the optional chat widget failing never reloads the whole page.
    return this.#skynetHttp.get('chat/user-chats-with-loan-headers', {
      skipResponseInterceptor: { forbiddenError: true },
    });
  }

  async createChat(body) {
    return this.#http.post(`api/Chats`, body);
  }

  async addCurrentUserToChat({ chatId }) {
    return this.#http.post(`api/Chats/${chatId}/members`);
  }

  // NOTE: the escalate endpoint ships with the TPO escalation engine (DEV-4223 Sub 1, Skynet.Notifications).
  // Callers must gate the Escalate control behind the tpoChat.escalate claim.
  async escalateChat({ chatId }) {
    return this.#http.post(`api/Chats/${chatId}/escalate`);
  }

  async getMessages({ chatId, before, limit }) {
    return this.#http.get(`api/Chats/${chatId}/messages`, { params: { before, limit } });
  }

  async sendMessage({
    chatId,
    senderId,
    payload,
    replyToMessageId = null,
    subjectRef = null,
    sendSmsForOfflineUsers,
  }) {
    return this.#http.post(
      `api/Chats/${chatId}/messages`,
      { senderId, payload, replyToMessageId, subjectRef },
      { headers: { sendSmsForOfflineUsers: sendSmsForOfflineUsers ? 'true' : 'false' } },
    );
  }

  async updateMessage({ chatId, messageId, senderId, payload }) {
    return this.#http.put(`api/Chats/${chatId}/messages/${messageId}`, { senderId, payload });
  }

  async getMessagesBySubjectPath({ chatId, subjectRefPath, before, limit }) {
    return this.#http.get(`api/Chats/${chatId}/messages`, {
      params: { before, limit, subjectRefPath },
    });
  }

  async markAsRead({ chatId, readAt }) {
    return this.#http.put(`api/Chats/${chatId}/read`, null, { params: { readAt } });
  }

  async getUnreadCount({ chatId }) {
    return this.#http.get(`api/Chats/${chatId}/unread-count`);
  }

  async addReaction({ chatId, messageId, emoji }) {
    return this.#http.post(`api/Chats/${chatId}/messages/${messageId}/reactions`, { emoji });
  }

  async removeReaction({ chatId, messageId, emoji }) {
    return this.#http.delete(
      `api/Chats/${chatId}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`,
    );
  }

  async uploadAttachment({ chatId, file, sendSmsForOfflineUsers }) {
    const formData = new FormData();

    formData.append('file', file);

    return this.#http.post(`api/Chats/${chatId}/messages/attachments`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        sendSmsForOfflineUsers: sendSmsForOfflineUsers ? 'true' : 'false',
      },
    });
  }

  async getPresence({ chatId }) {
    return this.#http.get(`api/Chats/${chatId}/presence`);
  }

  async pinMessage({ chatId, messageId }) {
    return this.#http.post(`api/Chats/${chatId}/messages/${messageId}/pin`);
  }

  async unpinMessage({ chatId, messageId }) {
    return this.#http.delete(`api/Chats/${chatId}/messages/${messageId}/pin`);
  }

  async getPinnedMessages({ chatId }) {
    return this.#http.get(`api/Chats/${chatId}/messages/pinned`);
  }

  async pinChat({ chatId }) {
    return this.#http.post(`api/Chats/${chatId}/pin`);
  }

  async unpinChat({ chatId }) {
    return this.#http.delete(`api/Chats/${chatId}/pin`);
  }

  async getUserMentions({ before = null, limit = 50 } = {}) {
    return this.#http.get('api/user/mentions', { params: { before, limit } });
  }
}
